import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type SectionGroup } from '@/components/Autocomplete/sectionUtils';
import { FormAutocompleteField } from '@/components/Form/FormAutocompleteField/FormAutocompleteField';
import { type Country, EntityType } from '@/core/graphql/generated/types';

import { pickCountryStepStyles as sx } from './PickCountryStep.styles';

const TOP_GDP_COUNTRY_IDS = [
	'us',
	'cn',
	'de',
	'jp',
	'in',
	'gb',
	'fr',
	'ca',
	'it',
	'br',
	'kr',
	'au',
	'ru',
	'mx',
	'es',
	'id',
	'nl',
	'sa',
	'tr',
	'ch'
] as const;

type Props = {
	entityType: EntityType;
	options: Country[];
	preferredCountryId?: string;
	value: string | undefined;
	onChange: (countryId: string | undefined) => void;
};

export const PickCountryStep = ({
	entityType,
	options,
	preferredCountryId,
	value,
	onChange
}: Props) => {
	const { t } = useTranslation();

	const suggested = preferredCountryId
		? options.find(c => c.id === preferredCountryId)
		: undefined;

	const topCountries = useMemo(
		() =>
			TOP_GDP_COUNTRY_IDS.map(id => options.find(c => c.id === id)).filter(
				(c): c is Country => c !== undefined
			),
		[options]
	);

	const sectionedOptions: SectionGroup<Country>[] = useMemo(
		() => [
			...(suggested
				? [{ key: 'suggested', label: t('Suggested'), options: [suggested] }]
				: []),
			...(topCountries.length > 0
				? [
						{
							key: 'top',
							label: t('Top countries'),
							options: topCountries
						}
					]
				: []),
			{ key: 'all', label: t('All countries'), options }
		],
		[options, suggested, t, topCountries]
	);

	return (
		<Box sx={sx.root}>
			<Typography variant='h6' component='h2' sx={sx.title}>
				{entityType === EntityType.Hcp
					? t("What is the HCP's country?")
					: t("What is the HCO's country?")}
			</Typography>
			<Box sx={sx.field}>
				<FormAutocompleteField<Country>
					label=''
					hideLabel
					placeholder={t('Select')}
					id='primaryCountry_V'
					name='primaryCountry_V'
					sectionedOptions={sectionedOptions}
					defaultValue={value ?? ''}
					handleOnDropdownItemSelect={item => onChange(item?.id || undefined)}
				/>
			</Box>
		</Box>
	);
};
